import { cn } from "@/lib/utils";
import { ReactNode } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsTablet } from "@/hooks/use-tablet";

interface ResponsiveActionBarProps {
  children: ReactNode;
  className?: string;
}

interface ResponsiveFormSectionProps {
  children: ReactNode;
  className?: string;
}

export function ResponsiveActionBar({ children, className }: ResponsiveActionBarProps) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // Wait until at least one of them is defined
  if (isMobile === undefined && isTablet === undefined) {
    return <div className="flex gap-2 border-t p-4 bg-background z-30 h-20 opacity-0 w-full">{children}</div>;
  }

  // Mobile: sticky to bottom (scoped to parent width)
  if (isMobile) {
    return (
      <div
        className={cn("flex justify-between gap-2 border-t p-4 bg-background z-30 sticky bottom-0 w-full", className)}
      >
        {children}
      </div>
    );
  }

  // Tablet: sticky to bottom (scoped to parent width)
  if (isTablet) {
    return (
      <div
        className={cn("flex justify-between gap-2 border-t p-4 bg-background z-30 sticky bottom-0 w-full", className)}
      >
        {children}
      </div>
    );
  }

  // Desktop: sticky at bottom
  return (
    <div className={cn("flex justify-between gap-2 border-t p-4 bg-background z-30 sticky bottom-0", className)}>
      {children}
    </div>
  );
}

export function ResponsiveFormSection({ children, className }: ResponsiveFormSectionProps) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  // Render safely even if undefined
  if (isMobile === undefined && isTablet === undefined) {
    return <section className={cn("pb-24 opacity-0", className)}>{children}</section>;
  }

  if (isMobile) {
    return <section className={cn("pb-24", className)}>{children}</section>;
  }

  if (isTablet) {
    return <section className={cn("pb-20", className)}>{children}</section>;
  }

  // Desktop
  return <section className={cn("pb-0", className)}>{children}</section>;
}
